import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useFileValidationRules } from 'utils/hooks/useFileHandling';
import { showError } from 'actions/actionCreators/snackbarActions';
import { UPLOAD_FILE_MUTATION } from '../../document/edit/documents/mutations';
import { convertImageToPdfBytes, uploadedFileIsImage } from 'utils/functions';

export function useBulkFileUpload({
  onStarting = _ => {},
  onCompleted = _ => {},
  onError = _ => {},
  setUploading,
  showNotifications = true
} = {}) {
  const [hasError, setHasError] = useState(false);
  const { validateFileExtension, validateFileSize } = useFileValidationRules();
  const [uploadFileMutation, { loading: uploading, error, ...rest }] = useMutation(UPLOAD_FILE_MUTATION, {
    onError: err => {
      onError(err);
      showNotifications && showError(err);
    },
    onCompleted: data => {
      onCompleted(data);
    },
    context: {
      headers: {
        'Apollo-Require-Preflight': 'true'
      }
    }
  });

  const uploadFile = useCallback(
    async (documentId, siteId, correlationId, file, t) => {
      if (!file) {
        setHasError(true);
        setUploading(false);
        return;
      }
      if (!validateFileExtension(file) || !validateFileSize(file)) {
        setHasError(true);
        setUploading(false);
        return;
      }
      onStarting(file);

      if (uploadedFileIsImage(file.name)) {
        let pdfBytes;
        try {
          pdfBytes = await convertImageToPdfBytes(file);
        } catch (error) {
          setUploading(false);
          throw new Error(t(error.message));
        }
        file = new File([new Blob([pdfBytes], { type: 'application/pdf' })], `${file.name.replace(/\.[^/.]+$/, '')}.pdf`);
      }

      uploadFileMutation({
        variables: {
          input: { documentId, siteId, model: { file, fileId: correlationId } }
        }
      });
    },
    [onStarting, validateFileExtension, validateFileSize, uploadFileMutation, setUploading]
  );

  return [uploadFile, { uploading, error, hasError, ...rest }];
}
